import { useEffect, useRef, useState } from "react";
import { listPendingProgress, subscribeDeviceProgress } from "../utils/deviceProgress";
import { syncPendingProgress } from "../utils/progressSync";

export function useProgressSync(owner?: string) {
    const identity = useRef(owner);
    identity.current = owner;
    const [state, setState] = useState<{ owner?: string; status: "idle" | "syncing" | "error" }>({ status: "idle" });
    const retry = useRef<() => void>(() => {});
    useEffect(() => {
        if (!owner || owner === "guest") { retry.current = () => {}; return; }
        let cancelled = false;
        let running = false;
        let failures = 0;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const active = () => !cancelled && identity.current === owner;
        const schedule = (delay: number) => {
            clearTimeout(timer);
            if (active() && navigator.onLine !== false) timer = setTimeout(() => { void run(); }, delay);
        };
        const run = async () => {
            if (!active() || running || navigator.onLine === false) return;
            running = true;
            setState({ owner, status: "syncing" });
            try {
                await syncPendingProgress(owner, active);
                if (!active()) return;
                failures = 0;
                setState({ owner, status: "idle" });
                if ((await listPendingProgress(owner)).length) schedule(250);
            } catch {
                if (active()) {
                    setState({ owner, status: "error" });
                    failures++;
                    schedule(Math.min(60_000, 2_000 * 2 ** Math.min(failures - 1, 5)));
                }
            } finally { running = false; }
        };
        retry.current = () => { failures = 0; schedule(0); };
        const onOnline = () => { failures = 0; schedule(0); };
        // The inactive path is a guard only: this listener is removed by the cleanup that follows any owner change,
        // and a write by a stale owner would be invisible anyway (status is "idle" for any other owner).
        const onOffline = () => { clearTimeout(timer); /* istanbul ignore else */ if (active()) setState({ owner, status: "idle" }); };
        const unsubscribe = subscribeDeviceProgress(id => {
            if (id === owner && !running && !failures) schedule(250);
        });
        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);
        schedule(0);
        return () => {
            cancelled = true; clearTimeout(timer); unsubscribe();
            window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline);
        };
    }, [owner]);
    return { status: state.owner === owner ? state.status : "idle", retry: () => retry.current() };
}
